import { Validate } from '../interface';

/**
 * 获取数据类型
 */
export function getDataType(val: unknown): string {
  return Object.prototype.toString
    .call(val)
    .toLowerCase()
    .replace(/(\[object\s|\])/g, '');
}

/**
 * 判断是否是数字类型
 */

const regPos = /^\d+(\.\d+)?$/; //非负浮点数
const regNeg = /^(-(([0-9]+\.[0-9]*[1-9][0-9]*)|([0-9]*[1-9][0-9]*\.[0-9]+)|([0-9]*[1-9][0-9]*)))$/; //负浮点数
export function isNumber(val: unknown): val is number {
  if (typeof val === 'string') return regPos.test(val) || regNeg.test(val);
  return getDataType(val) === Validate.DataTypes.number;
}

/**
 * 判断是否是字符串类型
 */
export function isString(val: unknown): val is string {
  return getDataType(val) === Validate.DataTypes.string;
}

/**
 * 判断是否是布尔类型
 */
export function isBoolean(val: unknown): val is boolean {
  return getDataType(val) === Validate.DataTypes.boolean;
}

/**
 * 判断是否为null
 */
export function isNull(val: unknown): val is null {
  return getDataType(val) === Validate.DataTypes.null;
}

/**
 * 判断是否为undefined
 */
export function isUndefined(val: unknown): val is undefined {
  return getDataType(val) === Validate.DataTypes.undefined;
}

/**
 * 判断是否为函数
 * @param val: 值
 * @param {boolean} options[isContainAsync] 默认 true 是否包含 Async 方法校验
 * @returns {boolean}
 */
export function isFunction(val: unknown, options?: { isContainAsync?: boolean }): val is Function {
  const _opt = {
    isContainAsync: true,
    ...(options ?? {}),
  };

  // 判断是否包含Async 方法校验
  return _opt.isContainAsync
    ? getDataType(val).includes(Validate.DataTypes.function)
    : getDataType(val) === Validate.DataTypes.function;
}

/**
 * 判断是否为数组
 */
export function isArray<T = any>(val: unknown): val is Array<T> {
  return getDataType(val) === Validate.DataTypes.array;
}

/**
 * 判断是否为时间类型
 */
export function isDate(val: unknown): val is Date {
  return getDataType(val) === Validate.DataTypes.date;
}

/**
 * 判断是否为正则类型
 */
export function isRegExp(val: unknown): val is RegExp {
  return getDataType(val) === Validate.DataTypes.regExp;
}
/**
 * 判断是否为NaN
 */
export function isNaN(val: unknown): val is number {
  return val !== val;
}

/**
 * 判断是否是对象
 * @param val
 * @param isExact 是否需要精确匹配（忽略数组，函数等）
 * @returns
 */
export function isObject<T = Record<string, unknown>>(val: unknown, isExact = false): val is T {
  if (isExact) return getDataType(val) === Validate.DataTypes.object;
  return !isNull(val) && typeof val === 'object';
}

/**
 * 校验是否为无效值：[NaN,空字符串，null, undefined]
 * @param {unknown} value
 * @param {boolean} ignoreString // 判断是否要忽略String
 * @returns
 */
export function isInvalid(value: unknown, ignoreString = false): boolean {
  const _invalid = isUndefined(value) || isNull(value) || isNaN(value);

  return ignoreString ? _invalid : _invalid || (isString(value) && !value.trim());
}

/**
 * 是否为异步方法
 */
export function isPromise<T = any>(val: any): val is Promise<T> {
  return isObject(val) && isFunction(val.then) && isFunction(val.catch);
}

/**
 * 判断是否是元素
 */
export function isElement(val: unknown): val is Element {
  return isObject(val) && !!val.tagName;
}

/**
 * 判断是否为空字段
 */
export function isEmpty<T = unknown>(val: T): val is T {
  if (isArray(val) || isString(val)) {
    return val.length === 0;
  }
  if (val instanceof Map || val instanceof Set) {
    return val.size === 0;
  }
  if (isObject(val)) {
    return Object.keys(val).length === 0;
  }
  return false;
}

const emailRegx = /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
/**
 * 判断是否为邮箱
 * @param value
 * @returns
 */
export function isEmail(value: string): boolean {
  return emailRegx.test(value);
}

/**
 * 判断是否都是对象的属性值
 * @returns
 */
export function isHasAllOwnProperty(keys: string[], obj: Record<any, any>): boolean {
  return keys.every(key => Object.prototype.hasOwnProperty.call(obj, key));
}
